import React, { useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import 'intl';
import { Helmet } from 'react-helmet';
import { useLocation } from '@reach/router';

import { useDataLayer } from '../hooks/useDataLayer';
import { useUseStaticQuery } from '../hooks/useUseStaticQuery';
import { useNavbar } from '../hooks/useNavbar';
import { useLanguage } from '../hooks/useLanguage';
import { usePaths } from '../hooks/usePaths';
import { useCustomBreadcrumb } from '../hooks/useCustomBreadcrumb';
import { useAxeptio } from '../hooks/useAxeptio';

import Navbar from './Navbar';
import ButtonScrollToTop from './ButtonScrollToTop';
import Footer from './Footer';

const Layout = ({ data, helmetMetadata, children }) => {
  const location = useLocation();

  // Get translated content
  const { translations, navbar, footer } = useUseStaticQuery();

  // DataLayer pageViews events
  useDataLayer(data);

  // Get langKey for IntlProvider
  const { langKey } = useLanguage();

  // Manage burger state
  const { isBurgerMenu } = useNavbar();

  // Set page contentful id
  const { setPageContentfulId, getPathObjectWithPathname } = usePaths();

  useEffect(() => {
    setPageContentfulId(data?.content?.contentful_id);
  }, [data]);

  //Provide breadcrumb to goole
  const { customCrumbs } = useCustomBreadcrumb();

  // Set up axeptio pop-up language
  useAxeptio();

  // Hide page when necessary
  const { isPageHiddenFromBots } = data?.content;

  return (
    <IntlProvider
      locale={langKey}
      key={langKey}
      messages={translations}
      defaultLocale="en"
    >
      <>
        <Helmet
          http-equiv="Content-Type"
          content="text/html; charset=UTF-8"
          key={location.href}
          title={helmetMetadata[3]?.content}
          defer={false}
          meta={helmetMetadata}
          htmlAttributes={{
            lang: langKey,
            style: isBurgerMenu ? `overflow: hidden` : null
          }}
        >
          {isPageHiddenFromBots && (
            <meta name="robots" content="noindex, nofollow" />
          )}
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'BreadcrumbList',
              itemListElement: customCrumbs?.map((crumb, i) => ({
                '@type': 'ListItem',
                position: i,
                name: getPathObjectWithPathname(crumb.pathname)?.name,
                item: crumb?.href
              }))
            })}
          </script>
        </Helmet>
        <Navbar navbar={navbar} />
        {children}
        <ButtonScrollToTop />
        <Footer footer={footer} />
      </>
    </IntlProvider>
  );
};

export default Layout;
